import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '@store/store.tsx';
import { selectSiteSettings } from '@store/slices/settingsSlice.tsx';
import { selectIsStaff } from '@store/slices/userSlice.tsx';
import { usePublicSitePreview } from '../../contexts/PublicSitePreviewContext.tsx';
import PublicSiteConstructionPage from './PublicSiteConstructionPage.tsx';

const isCmsPublicPath = (pathname: string): boolean => (
  pathname === '/web' || pathname.startsWith('/web/')
);

const isEshopPublicPath = (pathname: string): boolean => (
  pathname === '/eshop'
  || pathname.startsWith('/eshop/catalogue')
  || pathname.startsWith('/eshop/category/')
  || pathname.startsWith('/eshop/detail/')
  || pathname === '/eshop/cart'
  || pathname === '/eshop/checkout'
  || pathname.startsWith('/eshop/checkout/')
);

export const isPublicSitePath = (pathname: string): boolean => (
  isCmsPublicPath(pathname) || isEshopPublicPath(pathname)
);

interface Props {
  children?: ReactNode;
}

const PublicSiteAccessGate = ({ children }: Props) => {
  const { pathname } = useLocation();
  const site = useAppSelector(selectSiteSettings);
  const isStaff = useAppSelector(selectIsStaff);
  const isAuthenticated = useAppSelector((state) => state.auth?.isAuthenticated ?? false);
  const { previewActive, enterPreview } = usePublicSitePreview();
  const isEshop = isEshopPublicPath(pathname);
  const canPreview = isAuthenticated && isStaff;
  const isBlocked = Boolean(
    site?.public_under_construction
      && isPublicSitePath(pathname)
      && !(previewActive && canPreview),
  );

  if (isBlocked) {
    return (
      <PublicSiteConstructionPage
        site={site}
        isStaff={canPreview}
        isEshop={isEshop}
        onPreview={enterPreview}
      />
    );
  }

  return <>{children}</>;
};

export default PublicSiteAccessGate;
